import * as React from 'react';
import {FormattedMessage, InjectedIntl, InjectedIntlProps, injectIntl} from 'react-intl';
import {Field, Formik, FormikProps} from "formik";
import * as Yup from 'yup';
import viaForSchools from "../../assets/viaForSchools.svg";
import viaLogo from "../../assets/viaLogo.svg";
import {Button, CircularProgress, createStyles, TextField} from "@material-ui/core";
import {Autocomplete as FormikAutoComplete} from "formik-material-ui-lab";
import {RouteComponentProps, withRouter} from "react-router";
import {PageContainer, FormContainer, RowContainer} from '../common/styles';
import {makeStyles} from "@material-ui/core/styles";
import {useEffect, useState} from "react";
import {getCities} from "../../utils/get-cities-api";
import {Auth} from "aws-amplify";

const useStyles = makeStyles(() =>
    createStyles({
        chipItems: {
            '&:hover': {
                backgroundColor: '#EEFAFD',
            },
        },
        button: {
            width: "100%",
            marginTop: "5px",
            height: "40px",
            backgroundColor: "#20B7E9",
            color: "white"
        },
        buttonDisabled: {
            backgroundColor: "#dfdfdf",
        },
    }),
);

enum CitySelectorFormFields {
    City = 'City',
}

interface OwnProps {
    isViaForSchools: boolean;
    stage: string;
}

interface FormValues {
    [CitySelectorFormFields.City]: CityOption;
}

const getValidationSchema = (intl: InjectedIntl) => Yup.object().shape({
    [CitySelectorFormFields.City]: Yup.object().required(intl.messages['login.email.required']),
});

const initialValues: FormValues = {
    [CitySelectorFormFields.City]: null,
};

interface CityOption {
    name: string;
    code: string;
    city_id: string;
    tenant_id: string;
}

type Props = FormikProps<FormValues> & RouteComponentProps & OwnProps & InjectedIntlProps & RouteComponentProps;

const CitySelector = (props: Props) => {
    const classes = useStyles(props);
    const {intl, isViaForSchools, stage, history} = props;
    const [cities, setCities] = useState(null);

    const validationSchema = getValidationSchema(intl);

    const redirectToCity = (city: CityOption) => {
        window.location.replace(`https://${city.tenant_id}-${city.code}.voc.ridewithvia.com`);
    };

    const onSubmit = async (values) => {
        const city: CityOption = values[CitySelectorFormFields.City];
        redirectToCity(city);
    };

    useEffect(() => {
        Auth.currentSession()
            .then(() => {
                getCities(stage).then((res) => {
                    if (res.data.length === 1) {
                        redirectToCity(res.data[0]);
                    }
                    else {
                        setCities(res.data);
                    }
                });
            })
            .catch(() => {
                history.push("/login")
            })
    }, [history, stage]);

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
        >
            { props =>
                <PageContainer>
                    <FormContainer>
                        <RowContainer style={{backgroundColor: "#F3F5F8", margin: 0}}>
                            <div style={{padding: "20px", margin: "auto"}}>
                                {isViaForSchools ? <img alt="" src={viaForSchools}/> : <img alt="" src={viaLogo}/>}
                            </div>
                        </RowContainer>
                        {cities ?
                            <RowContainer style={{margin: "5px 70px 70px", flexDirection: "column"}}>
                                    <span style={{margin: "auto", padding: "10px", color: "#555"}}>
                                        <FormattedMessage id='city-selector.choose-city'/>
                                    </span>
                                <Field name={CitySelectorFormFields.City}
                                       component={FormikAutoComplete}
                                       getOptionLabel={(option: CityOption) => `${option.code.toUpperCase()} - ${option.name}`}
                                       style={{marginTop: "30px", marginBottom: "40px"}}
                                       label="Owner"
                                       size={"small"}
                                       onChange={(_, value) => {
                                           props.setFieldTouched(CitySelectorFormFields.City, true);
                                           props.setFieldValue(CitySelectorFormFields.City, value);
                                       }}
                                       classes={{option: classes.chipItems}}
                                       options={cities}
                                       renderInput={(params) => (
                                           <TextField {...params} label={intl.messages['city-selector.select-city']}
                                                      variant="outlined"/>
                                       )}
                                />
                                <Button
                                    type="submit"
                                    onClick={() => onSubmit(props.values)}
                                    classes={{disabled: classes.buttonDisabled}}
                                    disabled={!props.isValid}
                                    data-testid={`done-button`}
                                    className={classes.button}>
                                    <FormattedMessage id='city-selector.done-button'/>
                                </Button>
                            </RowContainer> :
                            <RowContainer style={{margin: "70px 70px 70px", flexDirection: "column"}}>
                                <CircularProgress style={{alignSelf: 'center'}} />
                            </RowContainer>
                        }
                    </FormContainer>
                </PageContainer>
            }
        </Formik>
    );
};

CitySelector.displayName = 'CitySelector';
export default withRouter(injectIntl(CitySelector));
